/**
 * Fix & enhance panel (WS F, plan §10.3 "Web", contract §6): per source, the detected issues in plain language with a
 * switch per correction, a 3-second before/after preview, the voice clean-up and upscaler providers with their tier
 * and cost, and "Reset to recommended". Every change is a PATCH of the source's overrides; drafts re-render.
 * Author: Eli Shvarzman
 */
import { useId, useMemo, useState } from 'react';
import { ChevronDown, Eye, LoaderCircle, RotateCcw, SlidersHorizontal } from 'lucide-react';
import type { CorrectionKey, CorrectionOverride, CorrectionProvider } from '@castfeeds/schema';
import { useT } from '@/i18n';
import { ErrorBox, Field, Ltr } from '@/components/ui';
import { usePatchCorrections, useRequestPreview } from '@/lib/api/hooks';
import { can } from '@/lib/constants';
import {
  describeSource, effectiveOf, hasVideo, isEdited, isVoiceSource, moreFixes, referenceOf, sourceName, upscalerOptions, voiceOptions,
  type ProviderOption, type SourceIssue,
} from '@/lib/enhance';
import { clipDuration } from '@/lib/format';
import { logError } from '@/lib/log';
import { useMembership } from '@/store/session';
import type { CorrectionsPatchBody, PipelineSession, SessionSource } from '@/lib/api/types';

const PREVIEW_MS = 3000;

/** Show the panel once any source of the session has been analysed. */
export const hasAnalysis = (s: PipelineSession | undefined) => !!s && s.sources.some((x) => !!x.analysis);

function StatusTag({ issue }: { issue: SourceIssue }) {
  const [bg, fg] = issue.key === null ? ['var(--amber-soft)', 'var(--amber)'] : issue.on ? ['var(--green-soft)', 'var(--green)'] : ['var(--raise)', 'var(--muted)'];
  return <span className="pill" style={{ background: bg, color: fg, flex: 'none' }}>{issue.status}</span>;
}

/** Compact switch on the shared `.toggle` styles; the visible issue text sits next to it. */
function Switch({ checked, label, disabled, onChange }: { checked: boolean; label: string; disabled?: boolean; onChange: (v: boolean) => void }) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} className="toggle" disabled={disabled} onClick={() => onChange(!checked)} style={{ flex: 'none' }}>
      <span className="toggle-track" aria-hidden />
    </button>
  );
}

function ProviderSelect<P extends CorrectionProvider>({ label, value, options, disabled, onChange }: { label: string; value: P; options: ProviderOption<P>[]; disabled?: boolean; onChange: (p: P) => void }) {
  const { t } = useT();
  const tierLabel = (tier: 'A' | 'B' | 'C') => (tier === 'A' ? t('fxTierA') : tier === 'B' ? t('fxTierB') : t('fxTierC'));
  return (
    <Field label={label} hint={t('fxProviderHint')}>
      {(p) => (
        <select {...p} className="select" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value as P)}>
          {options.map((o) => {
            const parts = [o.name, tierLabel(o.tier)];
            if (o.available) parts.push(t('fxFree'));
            else {
              if (o.pilot) parts.push(t('fxComing', { v: o.pilot }));
              if (o.cost.usd !== null) parts.push(t('fxCost', { usd: o.cost.usd.toFixed(2) }));
            }
            return <option key={o.value} value={o.value} disabled={!o.available}>{parts.join(' · ')}</option>;
          })}
        </select>
      )}
    </Field>
  );
}

function PreviewMedia({ source }: { source: SessionSource }) {
  const { t } = useT();
  const [broken, setBroken] = useState(false);
  const p = source.preview;
  if (!p) return null;
  if (p.status === 'queued' || p.status === 'running') {
    return <span role="status" className="muted small row"><LoaderCircle size={12} className="spin" aria-hidden />{t('fxPreviewing')}</span>;
  }
  if (p.status === 'failed') return <div role="alert" className="alert alert-error small">{t('fxPreviewFailed', { error: p.error ?? '—' })}</div>;
  if (!p.before_url || !p.after_url) return null;
  const name = sourceName(source);
  const onError = (which: string) => () => {
    logError('enhance', `cannot play preview ${which} source=${source.id}`);
    setBroken(true);
  };
  return (
    <div className="stack" style={{ gap: 6 }}>
      <span className="muted small">{t('fxBeforeAfter')}{p.at_ms !== null ? <> · <Ltr className="mono">{clipDuration(p.at_ms)}</Ltr></> : null}</span>
      {broken ? <div className="alert alert-warn small">{t('cantPlay')}</div> : null}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
        {([['before', t('fxBefore'), p.before_url], ['after', t('fxAfter'), p.after_url]] as const).map(([which, label, url]) => (
          <figure key={which} style={{ margin: 0, display: 'grid', gap: 4 }}>
            <figcaption className="small" style={{ fontWeight: 500 }}>{label}</figcaption>
            <div className="media" style={{ aspectRatio: '16/9', borderRadius: 4 }}>
              <video src={url} controls playsInline preload="metadata" aria-label={`${label} · ${name}`} style={{ width: '100%', height: '100%' }} onError={onError(which)} />
            </div>
          </figure>
        ))}
      </div>
    </div>
  );
}

function SourceCard({ session, source, episodeId, mayEdit }: { session: PipelineSession; source: SessionSource; episodeId: string; mayEdit: boolean }) {
  const { t, td } = useT();
  const titleId = useId();
  const patch = usePatchCorrections(session.id, episodeId);
  const preview = useRequestPreview(session.id);
  const [rerendered, setRerendered] = useState<number | null>(null);
  const reference = referenceOf(session.sources, source);
  const issues = useMemo(() => describeSource(source, reference, t, session.sources), [source, reference, t, session.sources]);
  const more = moreFixes(source, issues);
  const eff = effectiveOf(source);
  const name = sourceName(source);
  // While a save is in flight, show the switch where the person put it.
  const pending = patch.isPending ? patch.variables?.body.corrections : undefined;
  const shownOn = (key: CorrectionKey, on: boolean) => (pending && key in pending ? !!pending[key] : on);

  const send = (body: CorrectionsPatchBody) => {
    setRerendered(null);
    patch.mutate({ sourceId: source.id, body }, { onSuccess: (r) => setRerendered(r.rerendered.length) });
  };
  const toggle = (key: CorrectionKey, on: boolean) => send({ corrections: { [key]: on } as Record<CorrectionKey, CorrectionOverride> });
  const previewPending = source.preview?.status === 'queued' || source.preview?.status === 'running';
  const ingested = typeof source.duration_ms === 'number';
  const showVoice = isVoiceSource(source) || issues.some((i) => i.key === 'denoise_audio');
  const showUpscaler = hasVideo(source) && source.kind !== 'screen';
  const voice = (eff.denoise_audio || null)?.provider ?? (source.recommended_corrections?.denoise_audio || null)?.provider ?? 'ffmpeg';
  const scaler = (eff.upscale || null)?.provider ?? 'lanczos';
  const busy = !mayEdit || patch.isPending;

  return (
    <article className="card" aria-labelledby={titleId} style={{ padding: '14px 16px', display: 'grid', gap: 12, alignContent: 'start' }}>
      <div className="row wrap" style={{ gap: 8 }}>
        {/* Labels are typed by people (Hebrew or Latin): isolate them, direction from their own text. */}
        <h3 id={titleId} className="h2" style={{ fontSize: 14 }}><span dir="auto" style={{ unicodeBidi: 'isolate' }}>{name}</span></h3>
        <span className="tag">{td(`kind_${source.kind}`, source.kind)}</span>
        {source.person && source.person !== name ? <span className="muted small">{source.person}</span> : null}
        <span className="push row" style={{ gap: 6 }}>
          {typeof source.duration_ms === 'number' ? <Ltr className="mono muted small">{clipDuration(source.duration_ms)}</Ltr> : null}
          {source.analysis ? <span className="pill" style={{ background: 'var(--raise)', color: isEdited(source) ? 'var(--text)' : 'var(--muted)' }}>{isEdited(source) ? t('fxEdited') : t('fxRecommended')}</span> : null}
        </span>
      </div>

      {!source.analysis ? (
        <span className="muted small">{t('fxAnalysing')}</span>
      ) : issues.length === 0 ? (
        <span className="muted small">{t('fxNoIssues')}</span>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
          {issues.map((i) => {
            const on = i.key ? shownOn(i.key, i.on) : false;
            return (
              <li key={i.id} data-issue={i.id} className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
                {i.key ? <Switch checked={on} label={t('fxToggle', { issue: i.text })} disabled={busy} onChange={(v) => toggle(i.key!, v)} /> : <span aria-hidden style={{ width: 32, flex: 'none' }} />}
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, lineHeight: '19px' }}>{i.text}</span>
                <StatusTag issue={i.key ? { ...i, on, status: on === i.on ? i.status : on ? '…' : t('fxOff') } : i} />
              </li>
            );
          })}
        </ul>
      )}

      {source.analysis && more.length && mayEdit ? (
        <details>
          <summary className="small" style={{ cursor: 'pointer' }}><SlidersHorizontal size={12} aria-hidden /> {t('fxMore')}</summary>
          <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0, display: 'grid', gap: 8 }}>
            {more.map((key) => (
              <li key={key} className="row" style={{ gap: 10 }}>
                <Switch checked={shownOn(key, false)} label={t('fxToggle', { issue: t(`fxk_${key}` as const) })} disabled={busy} onChange={(v) => toggle(key, v)} />
                <span className="small">{t(`fxk_${key}` as const)}</span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {source.analysis && (showVoice || showUpscaler) ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
          {showVoice ? (
            <ProviderSelect label={t('fxVoice')} value={voice} options={voiceOptions(source.duration_ms)} disabled={busy} onChange={(provider) => send({ corrections: { denoise_audio: { provider } } })} />
          ) : null}
          {showUpscaler ? (
            <ProviderSelect label={t('fxUpscaler')} value={scaler} options={upscalerOptions(source.duration_ms)} disabled={busy} onChange={(provider) => send({ corrections: { upscale: { provider } } })} />
          ) : null}
        </div>
      ) : null}

      {source.corrections_warnings?.length ? (
        <div role="status" className="alert alert-warn small">
          {source.corrections_warnings.map((w) => <div key={w}>{w}</div>)}
        </div>
      ) : null}
      {patch.error ? <ErrorBox error={patch.error} /> : null}
      {preview.error ? <ErrorBox error={preview.error} /> : null}
      {rerendered !== null ? (
        <span role="status" className="small row" style={{ color: 'var(--green)' }}>
          {rerendered > 0 ? <LoaderCircle size={12} className="spin" aria-hidden /> : null}
          {rerendered === 0 ? t('fxRerenderNone') : rerendered === 1 ? t('fxRerenderingOne') : t('fxRerendering', { n: rerendered })}
        </span>
      ) : null}

      {source.analysis && mayEdit ? (
        <div className="row wrap" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn btn-sm"
            disabled={!ingested || previewPending || preview.isPending}
            onClick={() => preview.mutate({ sourceId: source.id, body: { duration_ms: PREVIEW_MS } })}
          >
            <Eye size={12} aria-hidden />
            {source.preview?.status === 'ready' || source.preview?.status === 'failed' ? t('fxPreviewAgain') : t('fxPreview')}
          </button>
          <button type="button" className="btn btn-sm btn-ghost" disabled={busy || !isEdited(source)} onClick={() => send({ reset: true })}>
            <RotateCcw size={12} aria-hidden />
            {t('fxReset')}
          </button>
          {!ingested ? <span className="muted small">{t('fxPreviewLater', { stage: td('stage_ingest') })}</span> : null}
        </div>
      ) : null}
      <PreviewMedia key={`${source.preview?.status}-${source.preview?.after_url ?? ''}`} source={source} />
    </article>
  );
}

/** The episode page's Fix & enhance section for the latest session. Collapsed by default, with a one-line summary. */
export function FixEnhancePanel({ session, episodeId }: { session: PipelineSession; episodeId: string }) {
  const { t } = useT();
  const membership = useMembership();
  const mayEdit = can(membership?.role, 'videos.edit');
  const [open, setOpen] = useState(false);
  const headId = useId();
  const bodyId = useId();
  const summary = useMemo(() => {
    let on = 0;
    let total = 0;
    let info = 0;
    for (const s of session.sources) {
      if (!s.analysis) continue;
      for (const i of describeSource(s, referenceOf(session.sources, s), t, session.sources)) {
        if (i.key === null) info++;
        else {
          total++;
          if (i.on) on++;
        }
      }
    }
    return { on, total, info };
  }, [session.sources, t]);

  return (
    <section className="card" aria-labelledby={headId}>
      <div className="row wrap" style={{ padding: '12px 16px', gap: 10 }}>
        <div className="stack" style={{ gap: 2, flex: 1, minWidth: 220 }}>
          <h2 id={headId} className="h2">{t('fxTitle')}</h2>
          <span className="muted small">
            {t('fxSummary', { on: summary.on, total: summary.total })}
            {summary.info ? ` · ${t('fxNeedsLook', { n: summary.info })}` : ''}
          </span>
        </div>
        <button type="button" className="btn btn-sm" aria-expanded={open} aria-controls={open ? bodyId : undefined} onClick={() => setOpen((v) => !v)}>
          <ChevronDown size={14} aria-hidden style={{ transform: open ? 'rotate(180deg)' : undefined, transition: 'transform 150ms' }} />
          {open ? t('fxHide') : t('fxShow')}
        </button>
      </div>
      {open ? (
        <div id={bodyId} style={{ padding: '0 16px 16px', display: 'grid', gap: 12, borderTop: '1px solid var(--line)' }}>
          <p className="muted small" style={{ margin: '12px 0 0' }}>{t('fxSub')}</p>
          {!mayEdit ? <div className="alert alert-info small">{t('fxReadOnly')}</div> : null}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))', gap: 12, alignItems: 'start' }}>
            {session.sources.map((s) => <SourceCard key={s.id} session={session} source={s} episodeId={episodeId} mayEdit={mayEdit} />)}
          </div>
        </div>
      ) : null}
    </section>
  );
}

